// @ts-nocheck
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Loader2, 
  Trophy, 
  Users, 
  GripVertical, 
  Shuffle, 
  RotateCcw,
  Save,
  ArrowUp,
  ArrowDown
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface Tournament {
  id: string;
  name: string;
  tournament_type: string;
  max_teams: number;
  seeding_type: string;
}

interface TeamData {
  id: string;
  team_name: string;
  seed_number: number;
  coach_name?: string;
}

interface TeamSeedingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  tournament: Tournament;
}

interface SortableTeamItemProps {
  team: TeamData;
  index: number;
  onSeedChange: (teamId: string, newSeed: number) => void;
  onMoveUp: (teamId: string) => void;
  onMoveDown: (teamId: string) => void;
  isFirst: boolean;
  isLast: boolean;
}

const SortableTeamItem: React.FC<SortableTeamItemProps> = ({ 
  team, 
  index, 
  onSeedChange, 
  onMoveUp, 
  onMoveDown,
  isFirst,
  isLast 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: team.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 border rounded-lg bg-card hover:bg-accent/50 transition-colors"
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab hover:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors"
      >
        <GripVertical className="h-5 w-5" />
      </div>
      
      <div className="flex items-center gap-2 min-w-[60px]">
        <Label htmlFor={`seed-${team.id}`} className="text-sm font-medium">
          Seed:
        </Label>
        <Input
          id={`seed-${team.id}`}
          type="number"
          min="1"
          value={team.seed_number}
          onChange={(e) => onSeedChange(team.id, parseInt(e.target.value) || 1)}
          className="w-16 h-8 text-center"
        />
      </div>

      <div className="flex-1">
        <div className="font-medium">{team.team_name}</div>
        {team.coach_name && (
          <div className="text-sm text-muted-foreground">Coach: {team.coach_name}</div>
        )}
      </div>

      <div className="flex gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onMoveUp(team.id)}
          disabled={isFirst}
          className="h-8 w-8 p-0"
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onMoveDown(team.id)}
          disabled={isLast}
          className="h-8 w-8 p-0"
        >
          <ArrowDown className="h-4 w-4" />
        </Button>
      </div>

      <Badge variant="secondary" className="min-w-[30px] justify-center">
        #{team.seed_number}
      </Badge>
    </div>
  );
};

export const TeamSeedingDialog: React.FC<TeamSeedingDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  tournament,
}) => {
  const [teams, setTeams] = useState<TeamData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchTeams = async () => {
    if (!tournament.id) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("tournament_brackets")
        .select(`
          id,
          team_name,
          seed_number,
          coach_name
        `)
        .eq("tournament_id", tournament.id)
        .order("seed_number");

      if (error) throw error;

      setTeams(data || []);
      setHasChanges(false);
    } catch (error: any) {
      console.error("Error fetching teams:", error);
      toast.error("Failed to load teams");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchTeams();
    }
  }, [isOpen, tournament.id]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = teams.findIndex((team) => team.id === active.id);
    const newIndex = teams.findIndex((team) => team.id === over.id);

    const newTeams = arrayMove(teams, oldIndex, newIndex);
    
    // Update seed numbers based on new positions
    const updatedTeams = newTeams.map((team, index) => ({
      ...team,
      seed_number: index + 1,
    }));

    setTeams(updatedTeams);
    setHasChanges(true);
  };

  const handleSeedChange = (teamId: string, newSeed: number) => {
    if (newSeed < 1 || newSeed > teams.length) return;

    const updatedTeams = [...teams];
    const teamIndex = updatedTeams.findIndex(t => t.id === teamId);
    const team = updatedTeams[teamIndex];
    const oldSeed = team.seed_number;

    // Update the team's seed number
    team.seed_number = newSeed;

    // Adjust other teams' seed numbers
    updatedTeams.forEach((t, index) => {
      if (t.id !== teamId) {
        if (newSeed > oldSeed) {
          // Moving down: shift teams up
          if (t.seed_number > oldSeed && t.seed_number <= newSeed) {
            t.seed_number--;
          }
        } else {
          // Moving up: shift teams down
          if (t.seed_number >= newSeed && t.seed_number < oldSeed) {
            t.seed_number++;
          }
        }
      }
    });

    // Sort by seed number
    updatedTeams.sort((a, b) => a.seed_number - b.seed_number);
    setTeams(updatedTeams);
    setHasChanges(true);
  };

  const handleMoveUp = (teamId: string) => {
    const teamIndex = teams.findIndex(t => t.id === teamId);
    if (teamIndex > 0) {
      const newTeams = arrayMove(teams, teamIndex, teamIndex - 1);
      const updatedTeams = newTeams.map((team, index) => ({
        ...team,
        seed_number: index + 1,
      }));
      setTeams(updatedTeams);
      setHasChanges(true);
    }
  };

  const handleMoveDown = (teamId: string) => {
    const teamIndex = teams.findIndex(t => t.id === teamId);
    if (teamIndex < teams.length - 1) {
      const newTeams = arrayMove(teams, teamIndex, teamIndex + 1);
      const updatedTeams = newTeams.map((team, index) => ({
        ...team,
        seed_number: index + 1,
      }));
      setTeams(updatedTeams);
      setHasChanges(true);
    }
  };

  const handleShuffle = () => {
    const shuffledTeams = [...teams];
    for (let i = shuffledTeams.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledTeams[i], shuffledTeams[j]] = [shuffledTeams[j], shuffledTeams[i]];
    }
    
    const updatedTeams = shuffledTeams.map((team, index) => ({
      ...team,
      seed_number: index + 1,
    }));

    setTeams(updatedTeams);
    setHasChanges(true);
    toast.success("Teams shuffled randomly");
  };

  const handleResetAlphabetical = () => {
    const sortedTeams = [...teams].sort((a, b) => a.team_name.localeCompare(b.team_name));
    const updatedTeams = sortedTeams.map((team, index) => ({
      ...team,
      seed_number: index + 1,
    }));

    setTeams(updatedTeams);
    setHasChanges(true);
    toast.success("Teams reset to alphabetical order");
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Update all teams' seed numbers
      const updates = teams.map(team => ({
        id: team.id,
        seed_number: team.seed_number,
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from("tournament_brackets")
          .update({ seed_number: update.seed_number })
          .eq("id", update.id);

        if (error) throw error;
      }

      toast.success("Team seeding saved successfully");
      setHasChanges(false);
      onSuccess();
    } catch (error: any) {
      console.error("Error saving team seeding:", error);
      toast.error("Failed to save team seeding");
    } finally {
      setIsSaving(false);
    }
  };

  const getTournamentTypeDisplay = (type: string) => {
    switch (type) {
      case "single_elimination":
        return "Single Elimination";
      case "double_elimination":
        return "Double Elimination";
      case "round_robin":
        return "Round Robin";
      case "pool_play_playoffs":
        return "Pool Play + Playoffs";
      default:
        return type;
    }
  };

  const getSeedingTypeDisplay = (type: string) => {
    switch (type) {
      case "manual":
        return "Manual";
      case "random":
        return "Random";
      case "ranking_based":
        return "Ranking-based";
      default:
        return type;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Manage Team Seeding - {tournament.name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col space-y-4">
          {/* Tournament Info */}
          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium text-muted-foreground">Type:</span>
                  <div>{getTournamentTypeDisplay(tournament.tournament_type)}</div>
                </div>
                <div>
                  <span className="font-medium text-muted-foreground">Teams:</span>
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {teams.length}/{tournament.max_teams}
                  </div>
                </div>
                <div>
                  <span className="font-medium text-muted-foreground">Seeding:</span>
                  <div>{getSeedingTypeDisplay(tournament.seeding_type)}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleShuffle}
                disabled={isLoading || isSaving}
                className="flex items-center gap-2"
              >
                <Shuffle className="h-4 w-4" />
                Auto-Shuffle
              </Button>
              <Button
                variant="outline"
                onClick={handleResetAlphabetical}
                disabled={isLoading || isSaving}
                className="flex items-center gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Reset Alphabetical
              </Button>
            </div>
            
            {hasChanges && (
              <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                Unsaved Changes
              </Badge>
            )}
          </div>

          {/* Teams List */}
          <Card className="flex-1 overflow-hidden">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5" />
                Team Seeding Order
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Drag teams to reorder, or use the arrow buttons and seed number inputs
              </p>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : teams.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No teams imported</h3>
                  <p className="text-muted-foreground">
                    Import teams to this tournament before managing seeding
                  </p>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={teams.map(team => team.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {teams.map((team, index) => (
                        <SortableTeamItem
                          key={team.id}
                          team={team}
                          index={index}
                          onSeedChange={handleSeedChange}
                          onMoveUp={handleMoveUp}
                          onMoveDown={handleMoveDown}
                          isFirst={index === 0}
                          isLast={index === teams.length - 1}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={onClose} 
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={isSaving || !hasChanges || teams.length === 0}
              className="flex items-center gap-2"
            >
              {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              <Save className="h-4 w-4" />
              Save Seeding
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};